import { db } from '../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

class AdminSettingsService {
  constructor() {
    this.settingsDocId = 'netatmo_settings';
    this.collectionName = 'admin_settings';
    this.localStorageKey = 'netatmo_admin_settings';
  }

  // Fallback vers localStorage si Firebase ne fonctionne pas
  async saveToLocalStorage(settings) {
    try {
      const dataToSave = {
        clientId: settings.clientId?.trim() || '',
        clientSecret: settings.clientSecret?.trim() || '',
        redirectUri: settings.redirectUri?.trim() || `${window.location.origin}/rain/callback`,
        updatedAt: new Date().toISOString(),
        source: 'localStorage'
      };
      
      localStorage.setItem(this.localStorageKey, JSON.stringify(dataToSave));
      
      return {
        success: true,
        message: 'Paramètres sauvegardés localement (Firebase indisponible)'
      };
    } catch (error) {
      throw new Error('Impossible de sauvegarder même en local');
    }
  }

  // Lire depuis localStorage
  async getFromLocalStorage() {
    try {
      const stored = localStorage.getItem(this.localStorageKey);
      if (stored) {
        const data = JSON.parse(stored);
        return {
          clientId: data.clientId || '',
          clientSecret: data.clientSecret || '',
          redirectUri: data.redirectUri || `${window.location.origin}/rain/callback`,
          isConfigured: !!(data.clientId && data.clientSecret),
          source: 'localStorage'
        };
      }
    } catch (error) {
    }
    
    return {
      clientId: '',
      clientSecret: '',
      redirectUri: `${window.location.origin}/rain/callback`,
      isConfigured: false,
      source: 'default'
    };
  }

  // Récupérer les paramètres Netatmo depuis Firebase avec fallback localStorage
  async getNetatmoSettings() {
    try {
      
      // Vérifier que la base de données est disponible
      if (!db) {
        return await this.getFromLocalStorage();
      }

      const docRef = doc(db, this.collectionName, this.settingsDocId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        const settings = {
          clientId: data.clientId || '',
          clientSecret: data.clientSecret || '',
          redirectUri: data.redirectUri || `${window.location.origin}/rain/callback`,
          isConfigured: !!(data.clientId && data.clientSecret && data.clientId.trim() !== ''),
          source: 'firebase'
        };
        
        
        return settings;
      } else {
        // Essayer localStorage en fallback
        const localData = await this.getFromLocalStorage();
        if (localData.isConfigured) {
          return localData;
        }
        
        // Retourner la configuration par défaut si aucune configuration n'existe
        return {
          clientId: '',
          clientSecret: '',
          redirectUri: `${window.location.origin}/rain/callback`,
          isConfigured: false,
          source: 'default'
        };
      }
    } catch (error) {
      console.error('Erreur Firestore, tentative de fallback vers localStorage:', error);
      
      // Fallback vers localStorage en cas d'erreur Firebase
      try {
        return await this.getFromLocalStorage();
      } catch (localError) {
        console.error('Erreur localStorage aussi:', localError);
        throw new Error('Impossible de récupérer les paramètres depuis Firebase ou localStorage');
      }
    }
  }

  // Sauvegarder les paramètres Netatmo dans Firebase avec fallback localStorage
  async saveNetatmoSettings(settings) {

    // Essayer Firebase d'abord
    try {
      // Vérifier que la base de données est disponible
      if (!db) {
        return await this.saveToLocalStorage(settings);
      }

      const docRef = doc(db, this.collectionName, this.settingsDocId);
      
      const dataToSave = {
        clientId: settings.clientId?.trim() || '',
        clientSecret: settings.clientSecret?.trim() || '',
        redirectUri: settings.redirectUri?.trim() || `${window.location.origin}/rain/callback`,
        updatedAt: new Date().toISOString(),
        updatedBy: 'admin'
      };

      await setDoc(docRef, dataToSave, { merge: true });
      
      
      return {
        success: true,
        message: 'Paramètres Netatmo sauvegardés avec succès dans Firebase'
      };
    } catch (error) {
      console.error('Erreur Firestore, tentative de fallback vers localStorage:', {
        message: error.message,
        code: error.code
      });
      
      // Fallback vers localStorage
      try {
        return await this.saveToLocalStorage(settings);
      } catch (localError) {
        console.error('Erreur localStorage aussi:', localError);
        
        // Message d'erreur détaillé
        let errorMessage = 'Impossible de sauvegarder les paramètres';
        
        if (error.code === 'permission-denied') {
          errorMessage = 'Permissions Firestore insuffisantes et localStorage échoué. Contactez l\'administrateur.';
        } else if (error.code === 'unavailable') {
          errorMessage = 'Firebase indisponible et localStorage échoué. Réessayez plus tard.';
        } else {
          errorMessage = `Erreur Firebase (${error.message}) et localStorage échoué.`;
        }
        
        throw new Error(errorMessage);
      }
    }
  }

  // Valider les paramètres Netatmo
  validateNetatmoSettings(settings) {
    const errors = [];

    if (!settings.clientId?.trim()) {
      errors.push('Le Client ID est requis');
    } else if (settings.clientId.length < 10) {
      errors.push('Le Client ID semble trop court');
    }

    if (!settings.clientSecret?.trim()) {
      errors.push('Le Client Secret est requis');
    } else if (settings.clientSecret.length < 10) {
      errors.push('Le Client Secret semble trop court');
    }

    if (!settings.redirectUri?.trim()) {
      errors.push('L\'URL de redirection est requise');
    } else {
      try {
        const url = new URL(settings.redirectUri);
        
        // Vérifier que l'URL est en HTTPS ou localhost
        if (url.protocol !== 'https:' && url.hostname !== 'localhost' && url.hostname !== '127.0.0.1') {
          errors.push('L\'URL de redirection doit être en HTTPS ou localhost');
        }
        
        // Vérifier que le chemin se termine par /rain/callback
        if (!url.pathname.endsWith('/rain/callback')) {
          errors.push('L\'URL de redirection doit se terminer par /rain/callback');
        }
        
        // Avertissement si l'URL ne correspond pas à l'origin actuel
        const currentOrigin = window.location.origin;
        const expectedUri = `${currentOrigin}/rain/callback`;
        if (settings.redirectUri !== expectedUri) {
          errors.push(`ATTENTION: L'URL recommandée est ${expectedUri}`);
        }
        
      } catch (e) {
        errors.push('L\'URL de redirection n\'est pas valide');
      }
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  // Tester la connexion avec les paramètres fournis
  async testNetatmoConnection(settings) {
    try {
      // Construire l'URL de test (juste pour vérifier la structure)
      const authUrl = new URL('https://api.netatmo.com/oauth2/authorize');
      authUrl.searchParams.set('client_id', settings.clientId);
      authUrl.searchParams.set('redirect_uri', settings.redirectUri);
      authUrl.searchParams.set('scope', 'read_station');
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('state', 'test');

      // Pour l'instant, on valide juste que l'URL peut être construite
      // Dans un vrai test, on pourrait faire un appel ping à l'API Netatmo
      return {
        success: true,
        message: 'Paramètres validés - URL d\'autorisation construite avec succès',
        authUrl: authUrl.toString()
      };
    } catch (error) {
      return {
        success: false,
        message: `Erreur de configuration: ${error.message}`
      };
    }
  }

  // Réinitialiser la configuration Netatmo
  async resetNetatmoSettings() {
    try {
      const docRef = doc(db, this.collectionName, this.settingsDocId);
      
      const resetData = {
        clientId: '',
        clientSecret: '',
        redirectUri: `${window.location.origin}/rain/callback`,
        updatedAt: new Date().toISOString(),
        updatedBy: 'admin',
        resetAt: new Date().toISOString()
      };

      await setDoc(docRef, resetData, { merge: true });
      
      return {
        success: true,
        message: 'Configuration Netatmo réinitialisée'
      };
    } catch (error) {
      console.error('Erreur lors de la réinitialisation:', error);
      throw new Error('Impossible de réinitialiser la configuration');
    }
  }

  // Obtenir le statut de la configuration
  async getConfigurationStatus() {
    try {
      const settings = await this.getNetatmoSettings();
      
      return {
        isConfigured: settings.isConfigured,
        hasClientId: !!settings.clientId,
        hasClientSecret: !!settings.clientSecret,
        hasRedirectUri: !!settings.redirectUri,
        redirectUri: settings.redirectUri
      };
    } catch (error) {
      return {
        isConfigured: false,
        hasClientId: false,
        hasClientSecret: false,
        hasRedirectUri: false,
        error: error.message
      };
    }
  }

  // Exporter la configuration (pour sauvegarde)
  async exportConfiguration() {
    try {
      const settings = await this.getNetatmoSettings();
      
      // Ne pas exporter le client secret pour la sécurité
      return {
        clientId: settings.clientId,
        redirectUri: settings.redirectUri,
        exportedAt: new Date().toISOString()
      };
    } catch (error) {
      throw new Error('Impossible d\'exporter la configuration');
    }
  }

  // Test de diagnostic pour vérifier les accès
  async diagnosticTest() {
    const results = {
      firebase: { canRead: false, canWrite: false, error: null },
      localStorage: { canRead: false, canWrite: false, error: null }
    };

    // Test Firebase
    try {
      if (db) {
        // Test de lecture
        const docRef = doc(db, this.collectionName, 'test_diagnostic');
        await getDoc(docRef);
        results.firebase.canRead = true;

        // Test d'écriture
        await setDoc(docRef, { 
          test: true, 
          timestamp: new Date().toISOString() 
        }, { merge: true });
        results.firebase.canWrite = true;
      } else {
        results.firebase.error = 'Firebase non initialisé';
      }
    } catch (error) {
      results.firebase.error = error.message;
    }

    // Test localStorage
    try {
      const testKey = 'netatmo_diagnostic_test';
      const testData = { test: true, timestamp: new Date().toISOString() };
      
      // Test d'écriture
      localStorage.setItem(testKey, JSON.stringify(testData));
      results.localStorage.canWrite = true;
      
      // Test de lecture
      const stored = localStorage.getItem(testKey);
      if (stored) {
        JSON.parse(stored);
        results.localStorage.canRead = true;
      }
      
      // Nettoyage
      localStorage.removeItem(testKey);
    } catch (error) {
      results.localStorage.error = error.message;
    }

    return results;
  }

  // === AUTOMOWER SETTINGS ===

  /**
   * Récupérer les paramètres Automower depuis Firebase avec fallback localStorage
   */
  async getAutomowerSettings() {
    try {
      // Vérifier que la base de données est disponible
      if (!db) {
        return await this.getAutomowerFromLocalStorage();
      }

      const docRef = doc(db, this.collectionName, 'automower_settings');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        const settings = {
          apiKey: data.apiKey || '',
          clientId: data.clientId || '',
          clientSecret: data.clientSecret || '',
          redirectUri: data.redirectUri || 'https://us-central1-theapp-428906.cloudfunctions.net/automowerOAuthCallback',
          isConfigured: !!(data.clientId && data.clientSecret && data.clientId.trim() !== ''),
          source: 'firebase'
        };
        
        return settings;
      } else {
        // Essayer localStorage en fallback
        const localData = await this.getAutomowerFromLocalStorage();
        if (localData.isConfigured) {
          return localData;
        }
        
        // Retourner la configuration par défaut si aucune configuration n'existe
        return {
          apiKey: '',
          clientId: '',
          clientSecret: '',
          redirectUri: 'https://us-central1-theapp-428906.cloudfunctions.net/automowerOAuthCallback',
          isConfigured: false,
          source: 'default'
        };
      }
    } catch (error) {
      console.error('Erreur Firestore, tentative de fallback vers localStorage:', error);
      
      // Fallback vers localStorage en cas d'erreur Firebase
      try {
        return await this.getAutomowerFromLocalStorage();
      } catch (localError) {
        console.error('Erreur localStorage aussi:', localError);
        throw new Error('Impossible de récupérer les paramètres depuis Firebase ou localStorage');
      }
    }
  }

  /**
   * Sauvegarder les paramètres Automower dans Firebase avec fallback localStorage
   */
  async saveAutomowerSettings(settings) {
    // Essayer Firebase d'abord
    try {
      // Vérifier que la base de données est disponible
      if (!db) {
        console.log('Firebase DB non disponible, utilisation localStorage');
        return await this.saveAutomowerToLocalStorage(settings);
      }

      const docRef = doc(db, this.collectionName, 'automower_settings');
      
      const dataToSave = {
        apiKey: settings.apiKey?.trim() || '',
        clientId: settings.clientId?.trim() || '',
        clientSecret: settings.clientSecret?.trim() || '',
        redirectUri: settings.redirectUri?.trim() || 'https://us-central1-theapp-428906.cloudfunctions.net/automowerOAuthCallback',
        updatedAt: new Date().toISOString(),
        updatedBy: 'admin'
      };

      console.log('Sauvegarde Automower dans Firebase...', { docRef: docRef.path, dataToSave });
      await setDoc(docRef, dataToSave, { merge: true });
      
      // Vérifier que la sauvegarde a réussi en relisant le document
      try {
        const verifyDoc = await getDoc(docRef);
        if (verifyDoc.exists() && verifyDoc.data().updatedAt === dataToSave.updatedAt) {
          console.log('✅ Sauvegarde Automower confirmée dans Firebase');
          return {
            success: true,
            message: 'Paramètres Automower sauvegardés avec succès dans Firebase'
          };
        } else {
          throw new Error('Vérification de sauvegarde échouée');
        }
      } catch (verifyError) {
        console.warn('⚠️ Impossible de vérifier la sauvegarde, mais pas d\'erreur de setDoc:', verifyError.message);
        // Si la vérification échoue mais setDoc n'a pas échoué, on considère que c'est réussi
        return {
          success: true,
          message: 'Paramètres Automower sauvegardés dans Firebase (vérification incertaine)'
        };
      }
      
    } catch (error) {
      console.error('❌ Erreur Firestore lors de la sauvegarde:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      
      // Fallback vers localStorage seulement pour les vraies erreurs
      try {
        const result = await this.saveAutomowerToLocalStorage(settings);
        console.log('📱 Fallback localStorage réussi');
        return result;
      } catch (localError) {
        console.error('❌ Erreur localStorage aussi:', localError);
        throw new Error(`Impossible de sauvegarder les paramètres: ${error.message}`);
      }
    }
  }

  /**
   * Sauvegarder en localStorage (Automower)
   */
  async saveAutomowerToLocalStorage(settings) {
    try {
      const dataToSave = {
        clientId: settings.clientId?.trim() || '',
        clientSecret: settings.clientSecret?.trim() || '',
        redirectUri: settings.redirectUri?.trim() || 'https://us-central1-theapp-428906.cloudfunctions.net/automowerOAuthCallback',
        updatedAt: new Date().toISOString(),
        source: 'localStorage'
      };
      
      localStorage.setItem('automower_admin_settings', JSON.stringify(dataToSave));
      
      return {
        success: true,
        message: 'Paramètres Automower sauvegardés localement (Firebase indisponible)'
      };
    } catch (error) {
      throw new Error('Impossible de sauvegarder même en local');
    }
  }

  /**
   * Lire depuis localStorage (Automower)
   */
  async getAutomowerFromLocalStorage() {
    try {
      const stored = localStorage.getItem('automower_admin_settings');
      if (stored) {
        const data = JSON.parse(stored);
        return {
          clientId: data.clientId || '',
          clientSecret: data.clientSecret || '',
          redirectUri: data.redirectUri || 'https://us-central1-theapp-428906.cloudfunctions.net/automowerOAuthCallback',
          isConfigured: !!(data.clientId && data.clientSecret),
          source: 'localStorage'
        };
      }
    } catch (error) {
      console.error('Erreur lecture localStorage Automower:', error);
    }
    
    return {
      clientId: '',
      clientSecret: '',
      redirectUri: 'https://us-central1-theapp-428906.cloudfunctions.net/automowerOAuthCallback',
      isConfigured: false,
      source: 'default'
    };
  }

  // === RAIN SETTINGS (DEFAULT STATION) ===

  /**
   * Récupérer les paramètres Rain (station par défaut) depuis Firebase
   */
  async getRainSettings() {
    try {
      if (!db) {
        return await this.getRainFromLocalStorage();
      }

      const docRef = doc(db, this.collectionName, 'rain_settings');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          defaultStation: data.defaultStation || null,
          source: 'firebase'
        };
      } else {
        // Fallback localStorage
        return await this.getRainFromLocalStorage();
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des paramètres Rain:', error);
      return await this.getRainFromLocalStorage();
    }
  }

  /**
   * Sauvegarder les paramètres Rain (station par défaut) dans Firebase
   */
  async saveRainSettings(settings) {
    try {
      if (!db) {
        return await this.saveRainToLocalStorage(settings);
      }

      const docRef = doc(db, this.collectionName, 'rain_settings');

      const dataToSave = {
        defaultStation: settings.defaultStation || null,
        updatedAt: new Date().toISOString(),
        updatedBy: 'admin'
      };

      await setDoc(docRef, dataToSave, { merge: true });

      // Sauvegarder aussi en localStorage pour fallback
      await this.saveRainToLocalStorage(settings);

      return {
        success: true,
        message: 'Station par défaut sauvegardée avec succès'
      };
    } catch (error) {
      console.error('Erreur Firestore, fallback localStorage:', error);
      return await this.saveRainToLocalStorage(settings);
    }
  }

  /**
   * Récupérer la station par défaut
   */
  async getDefaultStation() {
    try {
      const settings = await this.getRainSettings();
      return settings.defaultStation;
    } catch (error) {
      console.error('Erreur récupération station par défaut:', error);
      return null;
    }
  }

  /**
   * Définir la station par défaut
   */
  async setDefaultStation(stationId) {
    try {
      return await this.saveRainSettings({ defaultStation: stationId });
    } catch (error) {
      console.error('Erreur définition station par défaut:', error);
      throw error;
    }
  }

  /**
   * Lire depuis localStorage (Rain)
   */
  async getRainFromLocalStorage() {
    try {
      const stored = localStorage.getItem('rain_admin_settings');
      if (stored) {
        const data = JSON.parse(stored);
        return {
          defaultStation: data.defaultStation || null,
          source: 'localStorage'
        };
      }
    } catch (error) {
      console.error('Erreur lecture localStorage Rain:', error);
    }

    return {
      defaultStation: null,
      source: 'default'
    };
  }

  /**
   * Sauvegarder en localStorage (Rain)
   */
  async saveRainToLocalStorage(settings) {
    try {
      const dataToSave = {
        defaultStation: settings.defaultStation || null,
        updatedAt: new Date().toISOString(),
        source: 'localStorage'
      };

      localStorage.setItem('rain_admin_settings', JSON.stringify(dataToSave));

      return {
        success: true,
        message: 'Station par défaut sauvegardée localement'
      };
    } catch (error) {
      throw new Error('Impossible de sauvegarder la station par défaut');
    }
  }
}

// Instance singleton
const adminSettingsService = new AdminSettingsService();
export default adminSettingsService;